import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePromo } from '../context/PromoContext';
import TimeSelectionModal from './TimeSelectionModal';
import ItemCustomizationModal from './ItemCustomizationModal';
import { apiService } from '../services/api';
import { getAddonsFlat, getAddonsCount } from '../utils/addons';
import { isCafeOpenNow } from '../utils/workingHours';
import NotificationBell from './NotificationBell';
import '../styles/CartView.css';

const CART_SERVICE_NOTICE_SESSION_KEY = 'quickly_coffee_cart_service_notice_dismissed';
const NON_DRINK_MENU_TYPES = new Set(['еда', 'десерты', 'снэки']);

function isDrinkCartItem(item) {
  const type = String(item?.type || '').trim().toLowerCase();
  return !NON_DRINK_MENU_TYPES.has(type);
}

function getDrinksSubtotal(items = []) {
  return items.reduce((sum, item) => {
    if (!isDrinkCartItem(item)) return sum;
    return sum + Number(item.price || 0) * Number(item.quantity || 0);
  }, 0);
}

const CartView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editOrderId = searchParams.get('editOrderId');
  const { 
    carts,
    activeCafeId,
    setActiveCafe,
    removeItem,
    updateQuantity,
    updateItem,
    clearCafe,
    getTotalPrice,
    getTotalItems,
    getItemsForCafe,
    getCafeById,
    selectedCafe 
  } = useCart();
  const [imageErrors, setImageErrors] = useState({});
  const [isCartTotalExpanded, setIsCartTotalExpanded] = useState(true);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemFromCart, setEditingItemFromCart] = useState(null); // Сохраняем исходный товар из корзины
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);
  const [cafeOpenStatus, setCafeOpenStatus] = useState({ isOpen: true });
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [loyaltyPreview, setLoyaltyPreview] = useState(null);
  const [bonusSpendInput, setBonusSpendInput] = useState('');
  const [isLoadingLoyalty, setIsLoadingLoyalty] = useState(false);
  const [isServiceNoticeVisible, setIsServiceNoticeVisible] = useState(() => {
    try {
      return sessionStorage.getItem(CART_SERVICE_NOTICE_SESSION_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const cartItemsRef = useRef(null);
  const {
    appliedPromoCode,
    appliedPromoDiscount,
    setAppliedPromo,
    clearAppliedPromo,
  } = usePromo();
  const previousEditOrderId = useRef(null); // Отслеживаем предыдущий editOrderId

  const PROMO_REASON_MESSAGES = {
    not_found: 'Промокод не найден',
    inactive: 'Промокод деактивирован',
    expired: 'Срок действия промокода истёк',
    usage_limit_reached: 'Лимит использований промокода исчерпан',
    already_used: 'Вы уже использовали этот промокод',
    wrong_coffee_shop: 'Промокод не действует в этой кофейне',
    min_amount_not_reached: 'Минимальная сумма заказа для промокода не достигнута',
  };

  const handleApplyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoError('');
    setIsValidatingPromo(true);
    try {
      const subtotal = getTotalPrice(activeCafeKey);
      const res = await apiService.validatePromoCode(
        code,
        activeCafe?.id ?? null,
        subtotal
      );
      if (res && res.valid && res.code) {
        setAppliedPromo(res.code, res.discount_amount ?? null);
        setPromoInput('');
      } else {
        const reason = res?.reason || 'not_found';
        setPromoError(PROMO_REASON_MESSAGES[reason] || 'Промокод недействителен');
      }
    } catch (err) {
      setPromoError(err?.message || 'Ошибка проверки промокода');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const cafeEntries = useMemo(() => Object.entries(carts), [carts]);
  const hasAnyItems = cafeEntries.some(([, cart]) => cart.items.length > 0);

  // Очистка корзины при выходе из режима редактирования и восстановление резервной копии
  useEffect(() => {
    // Если был режим редактирования, а теперь его нет - восстанавливаем резервную копию корзины
    // Это происходит когда пользователь переходит на корзину БЕЗ editOrderId
    // или обновляет страницу без editOrderId
    if (previousEditOrderId.current !== null && !editOrderId && hasAnyItems) {
      // Пользователь покинул режим редактирования
      // Проверяем, есть ли резервная копия корзины
      const cartBackup = sessionStorage.getItem('quickly_coffee_cart_backup');
      
      if (cartBackup) {
        try {
          // Восстанавливаем резервную копию корзины
          const backupData = JSON.parse(cartBackup);
          if (backupData && backupData.carts && Object.keys(backupData.carts).length > 0) {
            // Очищаем текущую корзину
            Object.keys(carts).forEach(cafeId => {
              clearCafe(Number(cafeId));
            });
            
            // Восстанавливаем резервную копию в localStorage
            localStorage.setItem('quickly_coffee_cart', JSON.stringify(backupData));
            
            // Удаляем резервную копию из sessionStorage
            sessionStorage.removeItem('quickly_coffee_cart_backup');
            
            // Обновляем состояние через CartContext
            // Используем setTimeout, чтобы дождаться очистки корзины перед восстановлением
            setTimeout(() => {
              // Перезагружаем страницу для применения изменений из localStorage
              // Это необходимо, так как CartContext уже инициализирован с пустой корзиной
              window.location.reload();
            }, 100);
            
            if (process.env.NODE_ENV === 'development') {
              console.log('Restored cart backup after leaving edit mode, reloading page');
            }
            return; // Выходим из эффекта, чтобы избежать дальнейшей обработки
          } else {
            // Резервная копия пустая или невалидная - удаляем её
            sessionStorage.removeItem('quickly_coffee_cart_backup');
          }
        } catch (error) {
          console.error('Error restoring cart backup:', error);
          sessionStorage.removeItem('quickly_coffee_cart_backup');
        }
      }
      
      // Если резервной копии нет, просто очищаем корзину
      Object.keys(carts).forEach(cafeId => {
        clearCafe(Number(cafeId));
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('Clearing cart after leaving edit mode (no backup found)');
      }
    }
    // Обновляем предыдущий editOrderId
    previousEditOrderId.current = editOrderId;
  }, [editOrderId, hasAnyItems, carts, clearCafe]);

  useEffect(() => {
    if (!hasAnyItems) {
      return;
    }

    if (activeCafeId && carts[activeCafeId]?.items?.length > 0) {
      return;
    }

    const firstCafeWithItems = cafeEntries.find(([, cart]) => cart.items.length > 0);
    if (firstCafeWithItems) {
      setActiveCafe(Number(firstCafeWithItems[0]));
    }
  }, [activeCafeId, cafeEntries, carts, hasAnyItems, setActiveCafe]);

  const activeCart = useMemo(() => {
    if (activeCafeId && carts[activeCafeId]?.items?.length > 0) {
      return carts[activeCafeId];
    }
    const firstWithItems = cafeEntries.find(([, cart]) => cart.items.length > 0);
    return firstWithItems ? firstWithItems[1] : null;
  }, [activeCafeId, carts, cafeEntries]);

  const activeCafeKey = useMemo(() => {
    if (activeCafeId && carts[activeCafeId]?.items?.length > 0) {
      return activeCafeId;
    }
    const firstWithItems = cafeEntries.find(([, cart]) => cart.items.length > 0);
    return firstWithItems ? Number(firstWithItems[0]) : null;
  }, [activeCafeId, cafeEntries, carts]);

  const activeItems = activeCafeKey ? getItemsForCafe(activeCafeKey) : [];
  const activeCafe = activeCart?.cafe || (activeCafeKey ? getCafeById(activeCafeKey) : null);
  const offertaShopId = activeCafe?.id || selectedCafe?.id || null;

  const cartSubtotal = activeCafeKey ? getTotalPrice(activeCafeKey) : 0;
  const drinksSubtotal = useMemo(() => getDrinksSubtotal(activeItems), [activeItems]);
  const promoDiscountAmount =
    appliedPromoDiscount != null ? Math.min(appliedPromoDiscount, cartSubtotal) : 0;
  const requestedBonusSpend = Number(bonusSpendInput) > 0 ? Number(bonusSpendInput) : 0;

  useEffect(() => {
    if (!activeCafe?.id || cartSubtotal <= 0 || editOrderId) {
      setLoyaltyPreview(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingLoyalty(true);
      try {
        const preview = await apiService.previewLoyalty({
          coffeeShopId: activeCafe.id,
          subtotal: cartSubtotal,
          promoDiscount: promoDiscountAmount,
          bonusSpend: requestedBonusSpend,
          drinksSubtotal,
        });
        if (!cancelled) {
          setLoyaltyPreview(preview?.program_id ? preview : null);
          if (
            preview?.max_bonus_spend != null &&
            requestedBonusSpend > Number(preview.max_bonus_spend)
          ) {
            setBonusSpendInput(String(preview.max_bonus_spend));
          }
        }
      } catch (err) {
        if (!cancelled) setLoyaltyPreview(null);
      } finally {
        if (!cancelled) setIsLoadingLoyalty(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    activeCafe?.id,
    cartSubtotal,
    drinksSubtotal,
    promoDiscountAmount,
    requestedBonusSpend,
    editOrderId,
  ]);
  const offertaHref = offertaShopId ? `/offerta/${offertaShopId}` : '/offerta';

  // Загрузка статуса рабочих часов активной кофейни для блокировки оформления
  useEffect(() => {
    if (!activeCafeKey || activeItems.length === 0) {
      setCafeOpenStatus({ isOpen: true });
      return;
    }
    let cancelled = false;
    let cafeSnapshot = null;

    const applyStatus = () => {
      if (!cancelled && cafeSnapshot) {
        setCafeOpenStatus(isCafeOpenNow(cafeSnapshot));
      }
    };

    apiService.getCoffeeShop(activeCafeKey)
      .then((cafe) => {
        cafeSnapshot = cafe;
        applyStatus();
      })
      .catch(() => {
        if (!cancelled) setCafeOpenStatus({ isOpen: true });
      });

    const id = setInterval(applyStatus, 30000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') applyStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', applyStatus);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', applyStatus);
    };
  }, [activeCafeKey, activeItems.length]);

  const handleBackToMenu = () => {
    const cafeId = activeCafe?.id || selectedCafe?.id;
    if (cafeId) {
      // Сохраняем editOrderId в URL при переходе в меню
      const url = editOrderId 
        ? `/menu/${cafeId}?editOrderId=${editOrderId}`
        : `/menu/${cafeId}`;
      navigate(url);
      return;
    }

    navigate('/');
  };

  const handleBackToMap = () => {
    navigate('/');
  };

  const handleCheckout = async () => {
    if (!activeCafe || activeItems.length === 0) {
      return;
    }
    if (!cafeOpenStatus.isOpen) {
      alert(cafeOpenStatus.reason || 'Сейчас кофейня закрыта. Заказ недоступен.');
      return;
    }
    setIsTimeModalOpen(true);
  };

  const handleTimeSelected = async (timeSelection) => {
    setIsTimeModalOpen(false);
    setIsCreatingOrder(true);

    // Единообразно получаем ISO-время: и из своего ввода, и из готовых слотов
    const estimatedReadyTime =
      timeSelection?.time instanceof Date
        ? timeSelection.time.toISOString()
        : typeof timeSelection === 'string'
          ? timeSelection
          : null;

    if (!estimatedReadyTime) {
      setIsCreatingOrder(false);
      alert('Выберите время получения заказа.');
      setIsTimeModalOpen(true);
      return;
    }

    try {
      // Формируем данные заказа в нужном формате
      const orderPayload = {
        data: {
          coffee_shop_id: activeCafe.id,
          estimated_ready_time: estimatedReadyTime,
          ...(appliedPromoCode ? { promo_code: appliedPromoCode } : {}),
          ...(loyaltyPreview?.program_id && Number(bonusSpendInput) > 0
            ? { bonus_spend: Number(bonusSpendInput) }
            : {}),
        },
        items: activeItems.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          size_id: item.size_id || item.customizations?.sizeId || null,
          addon_ids: item.customizations?.addons?.map(a => a.id) || []
        }))
      };

      let orderId;

      if (editOrderId) {
        // Получаем текущий заказ, чтобы проверить его статус
        let currentOrder = null;
        try {
          currentOrder = await apiService.getOrder(editOrderId);
        } catch (err) {
          console.error('Error fetching current order:', err);
        }

        const updatePayload = {
          items: activeItems.map(item => {
            // Обрабатываем addon_ids - может быть массив объектов или массив ID
            let addonIds = [];
            if (item.customizations?.addons && Array.isArray(item.customizations.addons)) {
              addonIds = item.customizations.addons.map(a => 
                typeof a === 'object' && a !== null ? a.id : a
              ).filter(id => id != null && id !== undefined);
            } else if (item.addon_ids && Array.isArray(item.addon_ids)) {
              addonIds = item.addon_ids.map(id => 
                typeof id === 'object' && id !== null ? id.id : id
              ).filter(id => id != null && id !== undefined);
            }

            // Обрабатываем size_id
            let sizeId = null;
            if (item.size_id) {
              sizeId = item.size_id;
            } else if (item.customizations?.sizeId) {
              sizeId = item.customizations.sizeId;
            }

            // Валидация обязательных полей
            if (!item.id) {
              console.warn('Item missing id:', item);
            }
            if (!item.quantity || item.quantity <= 0) {
              console.warn('Item has invalid quantity:', item);
            }

            return {
              menu_item_id: item.id,
              quantity: item.quantity,
              size_id: sizeId,
              addon_ids: addonIds
            };
          }).filter(item => item.menu_item_id != null) // Убираем товары без ID
        };
        // Валидация: проверяем, что есть товары для обновления
        if (!updatePayload.items || updatePayload.items.length === 0) {
          throw new Error('Невозможно обновить заказ: корзина пуста');
        }

        // Простое обновление позиций заказа
        console.log('Updating order items:', editOrderId, updatePayload);
        const response = await apiService.updateOrder(editOrderId, updatePayload);
        console.log('Order updated successfully:', response);
        orderId = response.id || editOrderId;

        if (currentOrder && currentOrder.status === 'awaiting_confirmation') {
          try {
            console.log('Changing order status from awaiting_confirmation to pending');
            await apiService.updateOrderStatus(editOrderId, 'pending');
          } catch (statusError) {
            console.error('Error updating order status:', statusError);
          }
        }
      } else {
        // Режим создания - создаем новый заказ
        console.log('Creating order:', orderPayload);
        const response = await apiService.createOrder(orderPayload);
        console.log('Order created:', response);
        orderId = response.id || response.orderId;
      }
      
      // Очищаем корзину для этой кофейни и сбрасываем применённый промокод
      if (activeCafeId) {
        clearCafe(activeCafeId);
      }
      if (!editOrderId) {
        clearAppliedPromo();
      }

      // Перенаправляем на страницу статуса заказа
      if (orderId) {
        navigate(`/order/${orderId}`);
      } else {
        // Если по какой-то причине нет ID заказа, идем на главную
        navigate('/');
      }
    } catch (error) {
      console.error('Error processing order:', error);
      const isSessionExpired = error.message?.includes('Session expired') || error.message?.includes('authenticate');
      const errorMessage = isSessionExpired
        ? 'Сессия истекла. Откройте приложение заново из Telegram и повторите заказ.'
        : (error.message || 'Произошла неизвестная ошибка');
      alert(`Ошибка при ${editOrderId ? 'обновлении' : 'создании'} заказа: ${errorMessage}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleClearCart = () => {
    if (!activeCafeKey || activeItems.length === 0) {
      return;
    }

    if (window.confirm('Вы уверены, что хотите очистить корзину этой кофейни?')) {
      clearCafe(activeCafeKey);
    }
  };

  const handleImageError = (itemId) => {
    setImageErrors(prev => ({ ...prev, [itemId]: true }));
  };

  // Загрузка меню для редактирования товара
  const loadMenuForEditing = async (cafeId) => {
    if (menuItems.length > 0 && activeCafeKey === cafeId) {
      return menuItems;
    }

    try {
      setIsLoadingMenu(true);
      const items = await apiService.getMenuItems(cafeId);
      setMenuItems(items);
      return items;
    } catch (error) {
      console.error('Error loading menu for editing:', error);
      alert(`Ошибка при загрузке меню: ${error.message}`);
      return [];
    } finally {
      setIsLoadingMenu(false);
    }
  };

  // Обработчик редактирования товара
  const handleEditItem = async (item, index) => {
    if (!activeCafeKey || !activeCafe) {
      return;
    }

    try {
      // Загружаем актуальное меню для получения размеров и допников
      const items = await loadMenuForEditing(activeCafeKey);
      const menuItem = items.find(mi => mi.id === item.id);
      
      if (!menuItem) {
        alert('Товар больше не доступен в меню');
        return;
      }

      // Объединяем данные товара из корзины с актуальными данными из меню
      // Важно: сохраняем customizations из корзины, так как они содержат выбранные размеры и аддоны
      
      // Получаем аддоны из корзины (выбранные пользователем; поддержка сгруппированного формата API)
      let addonsFromCart = [];
      if (item.customizations?.addons && Array.isArray(item.customizations.addons) && item.customizations.addons.length > 0) {
        addonsFromCart = item.customizations.addons;
      } else if (getAddonsCount(item.addons) > 0) {
        addonsFromCart = getAddonsFlat(item.addons);
      }
      
      // Получаем размер из корзины
      const sizeIdFromCart = item.customizations?.sizeId || item.size_id || null;
      const sizeNameFromCart = item.customizations?.sizeName || item.size || null;
      const sizeVolumeWeightFromCart = item.customizations?.sizeVolumeWeight || null;
      
      const itemWithMenuData = {
        ...menuItem, // Берем актуальные sizes и addons из меню (для отображения в модалке)
        quantity: item.quantity,
        price: item.price,
        size: sizeNameFromCart,
        size_id: sizeIdFromCart,
        // Сохраняем все кастомизации из корзины (размер и аддоны)
        customizations: {
          sizeId: sizeIdFromCart,
          sizeName: sizeNameFromCart,
          sizeVolumeWeight: sizeVolumeWeightFromCart,
          // Сохраняем аддоны из корзины (они должны быть в формате массива объектов)
          addons: addonsFromCart
        },
        // Также сохраняем addon_ids для обратной совместимости
        addon_ids: item.addon_ids || addonsFromCart.map(a => (typeof a === 'object' ? a.id : a)).filter(id => id != null)
      };

      // Отладка: выводим данные товара для редактирования
      if (process.env.NODE_ENV === 'development') {
        console.log('Editing item - Original cart item:', {
          name: item.name,
          customizations: item.customizations,
          addon_ids: item.addon_ids,
          addons: item.addons,
          size_id: item.size_id,
          size: item.size
        });
        console.log('Editing item - Item with menu data:', {
          name: itemWithMenuData.name,
          customizations: itemWithMenuData.customizations,
          size_id: itemWithMenuData.size_id,
          addon_ids: itemWithMenuData.addon_ids,
          menuItemSizes: menuItem.sizes?.length || 0,
          menuItemAddons: getAddonsCount(menuItem.addons)
        });
      }

      // Сохраняем объединенные данные с меню для item (содержит актуальные sizes и addons)
      setEditingItem(itemWithMenuData);
      // Сохраняем исходный товар из корзины для initialItem (содержит правильные данные об аддонах)
      setEditingItemFromCart(item);
      setEditingItemIndex(index);
      setIsCustomizationOpen(true);
    } catch (error) {
      console.error('Error preparing item for editing:', error);
      alert(`Ошибка при подготовке товара для редактирования: ${error.message}`);
    }
  };

  // Обработчик обновления товара после редактирования
  const handleUpdateItem = (updatedItem) => {
    if (editingItemIndex !== null && activeCafeKey !== null) {
      updateItem(activeCafeKey, editingItemIndex, updatedItem);
      setIsCustomizationOpen(false);
      setEditingItem(null);
      setEditingItemIndex(null);
    }
  };

  // Обработчик закрытия модалки редактирования
  const handleCloseCustomization = () => {
    setIsCustomizationOpen(false);
    setEditingItem(null);
    setEditingItemFromCart(null);
    setEditingItemIndex(null);
  };

  // Обработчик клика на стрелку для переключения состояния
  const handleToggleExpanded = (e) => {
    e.stopPropagation();
    setIsCartTotalExpanded(!isCartTotalExpanded);
  };

  // Показываем cart-total при смене активной кофейни
  useEffect(() => {
    setIsCartTotalExpanded(true);
  }, [activeCafeKey]);

  const dismissServiceNotice = () => {
    try {
      sessionStorage.setItem(CART_SERVICE_NOTICE_SESSION_KEY, '1');
    } catch {
      /* ignore quota / private mode */
    }
    setIsServiceNoticeVisible(false);
  };

  const serviceNotice = isServiceNoticeVisible ? (
    <div className="cart-service-notice" role="status">
      <button
        type="button"
        className="cart-service-notice-close"
        onClick={dismissServiceNotice}
        aria-label="Закрыть уведомление"
      >
        ×
      </button>
      <div className="cart-service-notice-body">
        <strong>Новый подход!</strong>
        <p>
          Мы только начинаем работать с новым подходом. Просим снисходительно отнестись к возможным
          ошибкам и задержкам.
        </p>
        <p>
          По всем вопросам обращайтесь в Telegram:{' '}
          <a href="https://t.me/nshvora" target="_blank" rel="noopener noreferrer">
            @nshvora
          </a>
        </p>
      </div>
    </div>
  ) : null;

  if (!hasAnyItems) {
    return (
      <div className="cart-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>Корзина</h1>
              <p className="subtitle">Ваши заказы</p>
            </div>
            <div className="flex gap-2">
              {!editOrderId && (
              <button 
                className="btn btn-secondary cart-header-button" 
                onClick={handleBackToMap}
              >
                Карта
              </button>
              )}
              <button 
                className="btn btn-secondary cart-header-button" 
                onClick={handleBackToMenu}
              >
                Меню
              </button>
            </div>
          </div>
        </div>

        {serviceNotice}

        <div className="empty-state">
          <h3>Корзина пуста</h3>
          <p>Добавьте товары из меню, чтобы сделать заказ</p>
          <button className="btn" onClick={handleBackToMenu}>
            Перейти к меню
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <TimeSelectionModal 
        isOpen={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        onSelectTime={handleTimeSelected}
      />
      <ItemCustomizationModal
        item={editingItem}
        isOpen={isCustomizationOpen}
        onClose={handleCloseCustomization}
        onConfirm={() => {}} // Не используется в режиме редактирования
        editMode={true}
        initialItem={editingItemFromCart}
        onUpdate={handleUpdateItem}
      />

      <div className="header">
        <div className="flex space-between align-center">
          <div>
            {!editOrderId && (
              <button 
                className="btn cart-header-button" 
                onClick={handleBackToMenu}
              >
                Назад
              </button>
            )}
            {editOrderId && (
              <p style={{
                fontSize: '14px',
                color: '#ff9800',
                fontWeight: '500',
                background: 'rgba(255, 152, 0, 0.1)',
                padding: '6px 12px',
                borderRadius: '8px',
                display: 'inline-block',
                margin: 0
              }}>
                Редактирование заказа #{editOrderId}
              </p>
            )}
          </div>
          <h1 style={{ margin: 0 }}>{editOrderId ? 'Редактирование' : 'Корзина'}</h1>
          <div className="header-right-actions">
            <NotificationBell />
            {!editOrderId && (
              <button 
                className="btn cart-header-button" 
                onClick={handleBackToMap}
              >
                Карта
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="cart-coffee-shop-info">
        {cafeEntries.length > 0 && (
          <div className="cart-cafe-tabs">
            {cafeEntries.map(([cafeKey, cart]) => {
              const cafeData = cart.cafe;
              const isActive = Number(cafeKey) === Number(activeCafeKey);
              const totalItemsForCafe = getTotalItems(Number(cafeKey));

              return (
                <button
                  key={cafeKey}
                  className={`filter-btn cart-filter-btn ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveCafe(Number(cafeKey))}
                >
                  {cafeData?.name || `Кофейня #${cafeKey}`}
                  {totalItemsForCafe ? ` • ${totalItemsForCafe}` : ''}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {serviceNotice}
      <div 
        className="cart-items" 
        ref={cartItemsRef}
      >
        {activeItems.length === 0 ? (
          <div className="empty-state cart-empty-state-padding">
            <h3>В этой кофейне пока ничего нет</h3>
            <p>Добавьте товары из меню, чтобы оформить заказ</p>
          </div>
        ) : (
          activeItems.map((item, index) => {
            const key = `${activeCafeId}-${item.id}-${index}`;
            // Отладка: выводим структуру данных товара
            if (process.env.NODE_ENV === 'development') {
              console.log('Cart item display:', {
                name: item.name,
                customizations: item.customizations,
                customizations_addons: item.customizations?.addons,
                customizations_addons_length: item.customizations?.addons?.length || 0,
                size: item.size,
                size_id: item.size_id,
                addons: item.addons,
                addon_ids: item.addon_ids,
                has_customizations_addons: !!(item.customizations?.addons && item.customizations.addons.length > 0),
                has_addons: !!(Array.isArray(item.addons) && item.addons.length > 0)
              });
            }
            return (
              <div key={key} className="cart-item">
            <div className="cart-item-image">
                {imageErrors[key] ? (
                <div className="cart-item-image-placeholder">
                  <div className="cart-item-image-placeholder-icon"></div>
                  <div>Фото недоступно</div>
                </div>
                ) : (
                  <img 
                    src={item.image} 
                    alt={item.name}
                    onError={() => handleImageError(key)}
                  />
                )}
            </div>
            <div className="cart-item-info">
              <h4>{item.name}</h4>
              <p>{item.description}</p>
              {/* Отображение размера, если выбран */}
              {(item.customizations?.sizeName || item.size) && (
                <p className="cart-item-size" style={{ 
                  fontSize: '13px', 
                  color: '#666', 
                  marginTop: '4px',
                  marginBottom: '4px'
                }}>
                  Объем: {item.customizations?.sizeName || item.size}
                  {item.customizations?.sizeVolumeWeight && (
                    <span> ({item.customizations.sizeVolumeWeight})</span>
                  )}
                </p>
              )}
              {/* Отображение допников, если они есть */}
              {((item.customizations?.addons && Array.isArray(item.customizations.addons) && item.customizations.addons.length > 0) || 
                (Array.isArray(item.addons) && item.addons.length > 0) ||
                (Array.isArray(item.addon_ids) && item.addon_ids.length > 0)) && (
                <div className="cart-item-addons" style={{ 
                  marginTop: '6px',
                  marginBottom: '6px'
                }}>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#888', 
                    marginBottom: '4px',
                    fontWeight: '500'
                  }}>
                    Дополнительно:
                  </p>
                  <ul style={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    margin: 0,
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    {(() => {
                      // Поддержка разных форматов данных для аддонов
                      let addonsList = [];
                      
                      // Вариант 1: customizations.addons (массив объектов)
                      if (item.customizations?.addons && Array.isArray(item.customizations.addons)) {
                        addonsList = item.customizations.addons;
                      }
                      // Вариант 2: addons (поддержка сгруппированного и плоского формата API)
                      else if (getAddonsCount(item.addons) > 0) {
                        addonsList = getAddonsFlat(item.addons);
                      }
                      // Вариант 3: addon_ids (массив ID) — ищем аддоны в меню товара
                      else if (Array.isArray(item.addon_ids) && item.addon_ids.length > 0 && getAddonsCount(item.addons) > 0) {
                        const flatAddons = getAddonsFlat(item.addons);
                        addonsList = flatAddons.filter(a => item.addon_ids.includes(a.id));
                      }
                      
                      if (process.env.NODE_ENV === 'development' && addonsList.length === 0 && (item.customizations?.addons || item.addons || item.addon_ids)) {
                        console.warn('Addons found but empty list:', {
                          item_name: item.name,
                          customizations_addons: item.customizations?.addons,
                          addons: item.addons,
                          addon_ids: item.addon_ids
                        });
                      }
                      
                      return addonsList.map((addon, addonIndex) => {
                        // Поддержка разных форматов данных
                        const addonName = addon.name || addon.addon_name || 'Допник';
                        const addonPrice = addon.price || addon.addon_price || 0;
                        const addonVolume = addon.volume_weight || addon.volume || null;
                        
                        return (
                          <li key={addonIndex} style={{ 
                            marginBottom: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <span style={{ color: '#4caf50' }}>+</span>
                            <span>{addonName}</span>
                            {addonVolume && (
                              <span style={{ color: '#999', fontSize: '11px' }}>
                                ({addonVolume})
                              </span>
                            )}
                            <span style={{ color: '#888', marginLeft: 'auto' }}>
                              +{Number(addonPrice).toFixed(2)} ₽
                            </span>
                          </li>
                        );
                      });
                    })()}
                  </ul>
                </div>
              )}
              <p className="price-info">
                <span className="price-calculation">
                  <span className="price-item">{item.price} ₽</span>
                  <span className="price-separator"> × </span>
                  <span className="price-quantity">{item.quantity}</span>
                </span>
                <span className="price-separator"> = </span>
                <span className="price-total">{item.price * item.quantity} ₽</span>
              </p>
            </div>
              <div className="cart-item-controls">
              <div className="quantity-controls">
              <button 
                className="quantity-btn"
                    onClick={() => updateQuantity(activeCafeKey, index, item.quantity - 1)}
              >
                −
              </button>
              <span className="quantity">{item.quantity}</span>
              <button 
                className="quantity-btn"
                    onClick={() => updateQuantity(activeCafeKey, index, item.quantity + 1)}
              >
                +
              </button>
              </div>
              {/* Кнопка редактирования товара - показываем если есть кастомизации или если товар может иметь размеры/допники */}
              {(item.customizations || item.size_id || (item.addon_ids && item.addon_ids.length > 0) || item.sizes?.length > 0 || getAddonsCount(item.addons) > 0) && (
                <button 
                  className="quantity-btn cart-edit-btn"
                  onClick={() => handleEditItem(item, index)}
                  disabled={isLoadingMenu}
                  title="Изменить размер или допники"
                  style={{
                    cursor: isLoadingMenu ? 'not-allowed' : 'pointer',
                    opacity: isLoadingMenu ? 0.6 : 1
                  }}
                >
                  ✏️
                </button>
              )}
              <button 
                className="quantity-btn cart-remove-btn"
                onClick={() => removeItem(activeCafeKey, index)}
              >
                ×
              </button>
            </div>
            </div>
            );
          })
        )}
      </div>

      <div 
        className={`cart-total ${!isCartTotalExpanded ? 'collapsed' : ''}`}
      >
        <button 
          className="cart-toggle-arrow"
          onClick={handleToggleExpanded}
          aria-label={isCartTotalExpanded ? "Скрыть корзину" : "Показать корзину"}
        >
          {isCartTotalExpanded ? '▼' : '▲'}
        </button>
        <div className="cart-total-summary flex space-between align-center">
          <h3>Итого:</h3>
          <div className="total-price">
            {(() => {
              const subtotal = getTotalPrice(activeCafeKey);
              const discount = appliedPromoDiscount != null ? Math.min(appliedPromoDiscount, subtotal) : 0;
              const loyaltyDiscount = loyaltyPreview?.loyalty_discount_amount
                ? Number(loyaltyPreview.loyalty_discount_amount)
                : 0;
              const bonusSpent = loyaltyPreview?.bonus_spent
                ? Number(loyaltyPreview.bonus_spent)
                : 0;
              const total = Math.max(0, subtotal - discount - loyaltyDiscount - bonusSpent);
              return `${total.toFixed(2)} ₽`;
            })()}
          </div>
        </div>
        
        {isCartTotalExpanded && (
          <>
            {activeCafe && (
              <div className="cart-cafe-info-box">
                <p className="cart-cafe-info-text">
                  <strong>Кофейня:</strong> {activeCafe.name}
                </p>
                {activeCafe.address && (
                  <p className="cart-cafe-info-text">
                    {activeCafe.address}
                  </p>
                )}
              </div>
            )}

            {/* Блок промокода — валидация через GET /api/v1/promo-codes/validate/{code} */}
            <div className="cart-promo-box">
              {appliedPromoCode ? (
                <div className="cart-promo-applied">
                  <span className="cart-promo-applied-label">🏷️ Промокод применён:</span>
                  <span className="cart-promo-applied-code">{appliedPromoCode}</span>
                  {appliedPromoDiscount != null && (
                    <span className="cart-promo-discount">−{Math.min(appliedPromoDiscount, getTotalPrice(activeCafeKey)).toFixed(2)} ₽</span>
                  )}
                  <button
                    type="button"
                    className="cart-promo-remove-btn"
                    onClick={() => clearAppliedPromo()}
                  >
                    Отменить
                  </button>
                </div>
              ) : (
                <div className="cart-promo-form">
                  <label htmlFor="cart-promo-input" className="cart-promo-label">
                    Промокод
                  </label>
                  <div className="cart-promo-row">
                    <input
                      id="cart-promo-input"
                      type="text"
                      className="cart-promo-input"
                      placeholder="Введите промокод"
                      value={promoInput}
                      onChange={(e) => {
                        setPromoInput(e.target.value.trim().toUpperCase());
                        if (promoError) setPromoError('');
                      }}
                      maxLength={32}
                    />
                    <button
                      type="button"
                      className="cart-promo-apply-btn"
                      onClick={handleApplyPromo}
                      disabled={!promoInput.trim() || isValidatingPromo}
                    >
                      {isValidatingPromo ? 'Проверка...' : 'Применить'}
                    </button>
                  </div>
                  {promoError && (
                    <p className="cart-promo-error" role="alert">
                      {promoError}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Лояльность: скидка уровня/ДР + списание баллов */}
            {!editOrderId && loyaltyPreview?.program_id && (
              <div className="cart-loyalty-box">
                <div className="cart-loyalty-header">
                  <span className="cart-loyalty-title">Лояльность</span>
                  <span className="cart-loyalty-level">Ур. {loyaltyPreview.level}</span>
                </div>
                {loyaltyPreview.birthday_active && (
                  <p className="cart-loyalty-bday">Скидка на день рождения активна</p>
                )}
                {Number(loyaltyPreview.applied_discount_percent) > 0 && (
                  <p className="cart-loyalty-line">
                    Скидка {Number(loyaltyPreview.applied_discount_percent).toFixed(0)}%
                    {Number(loyaltyPreview.loyalty_discount_amount) > 0 && (
                      <span> −{Number(loyaltyPreview.loyalty_discount_amount).toFixed(2)} ₽</span>
                    )}
                  </p>
                )}
                {Number(loyaltyPreview.bonus_balance) > 0 || Number(loyaltyPreview.max_bonus_spend) > 0 ? (
                  <div className="cart-loyalty-bonus">
                    <div className="cart-loyalty-bonus-meta">
                      <span>Баллы: {Number(loyaltyPreview.bonus_balance).toFixed(0)}</span>
                      <span>
                        Можно списать до {Number(loyaltyPreview.max_bonus_spend).toFixed(0)}
                        {loyaltyPreview.bonus_debit_base === 'drinks_only' ? ' (напитки)' : ''}
                      </span>
                    </div>
                    <div className="cart-promo-row">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="cart-promo-input"
                        placeholder="Списать баллов"
                        value={bonusSpendInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setBonusSpendInput('');
                            return;
                          }
                          const max = Number(loyaltyPreview.max_bonus_spend) || 0;
                          const next = Math.max(0, Math.min(Number(raw) || 0, max));
                          setBonusSpendInput(String(next));
                        }}
                      />
                      <button
                        type="button"
                        className="cart-promo-apply-btn"
                        onClick={() =>
                          setBonusSpendInput(
                            String(Math.floor(Number(loyaltyPreview.max_bonus_spend) || 0)),
                          )
                        }
                        disabled={!(Number(loyaltyPreview.max_bonus_spend) > 0)}
                      >
                        Макс
                      </button>
                    </div>
                    {Number(loyaltyPreview.bonus_spent) > 0 && (
                      <p className="cart-loyalty-line">
                        Списание баллов: −{Number(loyaltyPreview.bonus_spent).toFixed(2)} ₽
                      </p>
                    )}
                  </div>
                ) : (
                  isLoadingLoyalty && (
                    <p className="cart-loyalty-line">Загрузка лояльности…</p>
                  )
                )}
                {loyaltyPreview.amount_to_next_level != null && loyaltyPreview.next_level && (
                  <p className="cart-loyalty-progress">
                    До ур. {loyaltyPreview.next_level}: ещё{' '}
                    {Number(loyaltyPreview.amount_to_next_level).toFixed(0)} ₽
                  </p>
                )}
              </div>
            )}

            {!cafeOpenStatus.isOpen && activeItems.length > 0 && (
              <div
                className="cart-closed-banner"
                style={{
                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                  color: '#fff',
                  padding: '10px 14px',
                  marginBottom: '12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  textAlign: 'center'
                }}
              >
                Сейчас кофейня закрыта. Оформление заказа временно недоступно.
                {cafeOpenStatus.todayHours && (
                  <div style={{ marginTop: '4px', opacity: 0.9, fontSize: '12px' }}>
                    {cafeOpenStatus.todayHours}
                  </div>
                )}
              </div>
            )}
            <div className="cart-actions">
              <button 
                className="btn btn-secondary"
                onClick={handleClearCart}
                disabled={!activeCafeKey || activeItems.length === 0}
              >
                Очистить
              </button>
              <button 
                className="btn"
                onClick={handleCheckout}
                disabled={isCreatingOrder || activeItems.length === 0 || !cafeOpenStatus.isOpen}
                title={!cafeOpenStatus.isOpen ? (cafeOpenStatus.reason || 'Кофейня закрыта') : undefined}
                style={!cafeOpenStatus.isOpen && activeItems.length > 0 ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
              >
                {isCreatingOrder 
                  ? (editOrderId ? 'Обновление...' : 'Создание...') 
                  : (editOrderId ? 'Сохранить изменения' : 'Оформить')
                }
              </button>
            </div>
            
            {!editOrderId && activeItems.length > 0 && (
              <div className="oferta-agreement">
                <p className="oferta-text">
                  Оформляя заказ, вы соглашаетесь с{' '}
                  <a 
                    href={offertaHref}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="oferta-link"
                  >
                    публичной офертой
                  </a>
                </p>
              </div>
            )}
          </>
        )}
        
        
      </div>
    </div>
  );
};

export default CartView;
